import { api } from './api';

export type BillTemplateKind = 'bill' | 'trust' | 'outstanding' | 'reminder';
export type BillSettingsOwnerType = 'chambers' | 'barrister';

export const TEMPLATE_KIND_LABELS: Record<BillTemplateKind, string> = {
  bill: 'Bill — covering email',
  trust: 'Trust deposit request',
  outstanding: 'Outstanding-balance chaser',
  reminder: 'Pre-due reminder',
};

export const ALLOWED_TEMPLATE_VARIABLES = [
  'bill_recipient_name',
  'bill_recipient_first_name',
  'firm_name',
  'firm_contact_name',
  'bill_number',
  'bill_total',
  'bill_amount_due',
  'bill_issue_date',
  'bill_due_date',
  'bill_memo',
  'grace_period',
  'days_overdue',
  'matter_number',
  'matter_name',
  'chambers_name',
] as const;

export interface BillTemplate {
  id: string;
  chambersId: string;
  ownerType: BillSettingsOwnerType;
  barristerOwnerId: string | null;
  name: string;
  kind: BillTemplateKind;
  subject: string;
  body: string;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  barristerOwner: {
    id: string;
    user: { id: string; firstName: string; lastName: string };
  } | null;
}

export interface CreateBillTemplatePayload {
  name: string;
  kind: BillTemplateKind;
  subject: string;
  body: string;
  ownerType?: BillSettingsOwnerType; // defaults to 'chambers'
  barristerOwnerId?: string | null;
  isDefault?: boolean;
}

export interface UpdateBillTemplatePayload {
  name?: string;
  subject?: string;
  body?: string;
  isDefault?: boolean;
  isActive?: boolean;
}

export interface ListBillTemplatesQuery {
  kind?: BillTemplateKind;
  scope?: 'all' | 'mine' | 'chambers';
  ownerType?: BillSettingsOwnerType;
  barristerOwnerId?: string;
  isActive?: 'true' | 'false';
}

export async function fetchBillTemplates(query: ListBillTemplatesQuery = {}): Promise<BillTemplate[]> {
  const res = await api.get<{ templates: BillTemplate[] }>('/bill-settings/templates', { params: query });
  return res.data.templates;
}

export async function createBillTemplate(payload: CreateBillTemplatePayload): Promise<BillTemplate> {
  const res = await api.post<{ template: BillTemplate }>('/bill-settings/templates', payload);
  return res.data.template;
}

export async function updateBillTemplate(id: string, payload: UpdateBillTemplatePayload): Promise<BillTemplate> {
  const res = await api.patch<{ template: BillTemplate }>(`/bill-settings/templates/${id}`, payload);
  return res.data.template;
}

export async function deleteBillTemplate(id: string): Promise<void> {
  await api.delete(`/bill-settings/templates/${id}`);
}

export interface BillReminderConfig {
  id: string | null;
  chambersId: string;
  ownerType: BillSettingsOwnerType;
  barristerOwnerId: string | null;
  enabled: boolean;
  sendCombinedEmail: boolean;
  firstReminderDays: number;
  repeatEveryDays: number;
  pauseOnPaymentPlan: boolean;
  repliesToUserId: string | null;
  templateId: string | null;
  repliesToUser: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  template: {
    id: string;
    name: string;
    kind: BillTemplateKind;
  } | null;
  isInitial: boolean;
}

export interface ReminderConfigOwner {
  ownerType: BillSettingsOwnerType;
  barristerOwnerId?: string | null;
}

export interface UpdateReminderConfigPayload extends ReminderConfigOwner {
  enabled?: boolean;
  sendCombinedEmail?: boolean;
  firstReminderDays?: number;
  repeatEveryDays?: number;
  pauseOnPaymentPlan?: boolean;
  repliesToUserId?: string | null;
  templateId?: string | null;
}

export async function fetchReminderConfig(owner: ReminderConfigOwner): Promise<BillReminderConfig> {
  const res = await api.get<{ config: BillReminderConfig }>('/bill-settings/reminder-config', {
    params: {
      ownerType: owner.ownerType,
      ...(owner.barristerOwnerId ? { barristerOwnerId: owner.barristerOwnerId } : {}),
    },
  });
  return res.data.config;
}

export async function updateReminderConfig(payload: UpdateReminderConfigPayload): Promise<BillReminderConfig> {
  const res = await api.patch<{ config: BillReminderConfig }>('/bill-settings/reminder-config', payload);
  return res.data.config;
}
